import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { coMaQuyen } from '../../../hooks/usePermission';
import { nktvNenTangRepository } from '../../../lib/repositories/nktv-nen-tang.repository';
import { nktvSuDungAiRepository } from '../../../lib/repositories/nktv-su-dung-ai.repository';
import { useAuthStore } from '../../../stores/auth';
import { useTenants } from '../../tenant/hooks/useTenants';
import { useTenDonVi } from '../chung/hooks/useTenDonVi';
import { DUONG_BANG_GIA_NEN_TANG } from '../chung/nghiep-vu/duong-dan';
import type { ThongKeAi, TruyVanThongKeAi } from '../chung/nghiep-vu/types-thong-ke';
import type {
  ChuoiVet,
  KetQuaLuong,
  TrangLuotGoi,
  TrangTrace,
  TruyVanLuong,
  TruyVanLuotGoi,
  TruyVanTrace,
  TuyChonLocAi,
} from '../chung/nghiep-vu/types-vet';

/**
 * Nguồn dữ liệu của "Nhật ký sử dụng AI" — một bộ component cho hai cổng:
 *
 * - Cổng tenant (thẻ của Cài đặt bảo mật): mặt module `/api/v1/m/audit/vet/*`, tenant lấy từ phiên.
 * - Cổng nền tảng (thẻ của Nhật ký hệ thống): `/api/v1/audit/ai-usage/*`, mọi tenant hoặc một tenant đang lọc.
 *
 * Hook dữ liệu (`hooks.ts`, `useThongKeAi`) đọc kho và tiền tố khoá truy vấn từ đây, nên hai cổng và từng tenant đang
 * lọc không dùng chung cache. Component đọc `nenTang` để thêm cột tenant, giá vốn và lối sang bảng giá model.
 */
export interface KhoVet {
  trace: (q: TruyVanTrace) => Promise<TrangTrace>;
  /** `tenantId` của dòng đã bấm — API nền tảng cần nó, mặt module bỏ qua. */
  chuoi: (traceId: string, tenantId: string | undefined) => Promise<ChuoiVet>;
  luotGoi: (q: TruyVanLuotGoi) => Promise<TrangLuotGoi>;
  boLoc: (q: { tu: string; den: string }) => Promise<TuyChonLocAi>;
  luong: (q: TruyVanLuong) => Promise<KetQuaLuong>;
  thongKeAi: (q: TruyVanThongKeAi) => Promise<ThongKeAi>;
}

export interface NguonVet {
  kho: KhoVet;
  /** Phần đầu khoá truy vấn, đứng trước mọi tham số lọc. */
  khoa: readonly unknown[];
  nenTang: boolean;
  /** Hiện cột và nhóm phân rã "Tenant": chỉ ở cổng nền tảng khi xem mọi tenant — đã lọc một tenant thì cột ấy chỉ lặp một tên. */
  cotTenant: boolean;
  /** `id → tên` phòng ban; chỉ cổng tenant đọc được danh bạ phòng ban. */
  tenDonVi: ReadonlyMap<string, string>;
  /** `tenant_id → tên hiển thị`; rỗng ở cổng tenant. */
  tenTenant: ReadonlyMap<string, string>;
  /** Đường gốc sang bảng giá model cho lượt thiếu giá; `null` khi người xem không mở được (và luôn ở cổng tenant). */
  lienKetBangGia: string | null;
}

const NguonVetContext = createContext<NguonVet | null>(null);

export function useNguonVet(): NguonVet {
  const nguon = useContext(NguonVetContext);
  if (!nguon) throw new Error('useNguonVet phải nằm trong NguonVetTenant hoặc NguonVetNenTang');
  return nguon;
}

/** Nhãn tenant của một dòng: tên hiển thị nếu đã tải, không thì tám ký tự đầu của mã. */
export function nhanTenant(tenTenant: ReadonlyMap<string, string>, tenantId: string | undefined): string {
  if (!tenantId) return '';
  return tenTenant.get(tenantId) ?? tenantId.slice(0, 8);
}

const KHO_TENANT: KhoVet = {
  trace: nktvSuDungAiRepository.trace,
  chuoi: (traceId) => nktvSuDungAiRepository.chuoi(traceId),
  luotGoi: nktvSuDungAiRepository.luotGoi,
  boLoc: nktvSuDungAiRepository.boLoc,
  luong: nktvSuDungAiRepository.luong,
  thongKeAi: nktvSuDungAiRepository.thongKeAi,
};

const KHONG_CO = new Map<string, string>();

export function NguonVetTenant({ children }: { children: ReactNode }) {
  const tenDonVi = useTenDonVi();
  const nguon = useMemo<NguonVet>(
    () => ({ kho: KHO_TENANT, khoa: ['tenant'], nenTang: false, cotTenant: false, tenDonVi, tenTenant: KHONG_CO, lienKetBangGia: null }),
    [tenDonVi],
  );
  return <NguonVetContext.Provider value={nguon}>{children}</NguonVetContext.Provider>;
}

/** `tenantId` bỏ trống là mọi tenant. */
export function NguonVetNenTang({ tenantId, children }: { tenantId: string | undefined; children: ReactNode }) {
  const qTenant = useTenants();
  const xemBangGia = useAuthStore((s) => coMaQuyen(s.user, 'AI_PRICING.READ'));
  const tenTenant = useMemo(
    () => new Map((qTenant.data ?? []).map((x) => [x.tenant_id, x.display_name])),
    [qTenant.data],
  );
  const nguon = useMemo<NguonVet>(() => {
    const r = nktvNenTangRepository;
    return {
      kho: {
        trace: (q) => r.trace(q, tenantId),
        chuoi: (traceId, tenantCuaDong) => r.chuoi(traceId, tenantCuaDong ?? tenantId ?? ''),
        luotGoi: (q) => r.luotGoi(q, tenantId),
        boLoc: (q) => r.boLoc(q, tenantId),
        luong: (q) => r.luong(q, tenantId),
        thongKeAi: (q) => r.thongKeAi(q, tenantId),
      },
      khoa: ['nen-tang', tenantId ?? '*'],
      nenTang: true,
      cotTenant: tenantId === undefined,
      tenDonVi: KHONG_CO,
      tenTenant,
      lienKetBangGia: xemBangGia ? DUONG_BANG_GIA_NEN_TANG : null,
    };
  }, [tenantId, tenTenant, xemBangGia]);
  return <NguonVetContext.Provider value={nguon}>{children}</NguonVetContext.Provider>;
}
